// Synchronizace s externími kalendáři (docs/PLAN.md 4.2): Google Calendar a Microsoft Outlook.
// Propojení je per uživatel v rámci firmy; události se synchronizují oběma směry přes frontu.
import { z } from 'zod'

export const CALENDAR_PROVIDERS = ['google', 'microsoft'] as const
export const calendarProviderSchema = z.enum(CALENDAR_PROVIDERS)
export type CalendarProvider = z.infer<typeof calendarProviderSchema>

/** both = oba směry (výchozí), push = jen z Wac Buddy ven, pull = jen z externího kalendáře dovnitř. */
export const CALENDAR_DIRECTIONS = ['both', 'push', 'pull'] as const
export const calendarDirectionSchema = z.enum(CALENDAR_DIRECTIONS)
export type CalendarDirection = z.infer<typeof calendarDirectionSchema>

/** Jak daleko do minulosti a budoucnosti se synchronizuje (dny). */
export const CALENDAR_SYNC_PAST_DAYS = 30
export const CALENDAR_SYNC_FUTURE_DAYS = 180

/** Kalendář u poskytovatele (GET /v1/me/calendars/:provider/calendars). */
export interface CalendarChoice {
  id: string
  name: string
  primary: boolean
  /** false = do kalendáře nejde zapisovat (jen pull). */
  writable: boolean
}

/** Stav propojení jednoho poskytovatele (GET /v1/me/calendars). */
export interface CalendarLinkStatus {
  provider: CalendarProvider
  /** false = poskytovatel není na serveru nastavený (chybí OAuth klíče), UI ukáže vysvětlení. */
  available: boolean
  /** Uživatel má u Better Auth propojený účet poskytovatele s právy ke kalendáři. */
  connected: boolean
  accountEmail: string | null
  /** Vybraný kalendář; null = propojeno, ale ještě nevybráno. */
  calendarId: string | null
  calendarName: string | null
  direction: CalendarDirection
  enabled: boolean
  lastSyncedAt: string | null
  lastError: string | null
  /** true = poslední běh selhal na autorizaci (vypršelý nebo odvolaný token): UI nabídne „Připojit znovu“. */
  needsReconnect: boolean
  /** Počet událostí importovaných z tohoto kalendáře (pro přehled v Nastavení). */
  importedEvents: number
}

export interface CalendarsResponse {
  data: CalendarLinkStatus[]
}

/** PUT /v1/me/calendars/:provider: výběr kalendáře a směru; založí nebo upraví propojení. */
export const updateCalendarLinkSchema = z.object({
  calendarId: z.string().trim().min(1).max(300).optional(),
  calendarName: z.string().trim().max(200).optional(),
  direction: calendarDirectionSchema.optional(),
  enabled: z.boolean().optional(),
})
export type UpdateCalendarLinkInput = z.infer<typeof updateCalendarLinkSchema>

/** Výsledek jednoho běhu synchronizace (POST /v1/me/calendars/:provider/sync i úloha fronty). */
export interface CalendarSyncResult {
  imported: number
  updated: number
  removed: number
  pushed: number
  /** Chyba poskytovatele (token, síť); propojení zůstává, zkusí se příště. */
  error: string | null
}

/** Odkaz, kam poslat uživatele na propojení účtu (GET /v1/me/calendars/:provider/connect-url). */
export interface CalendarConnectUrl {
  url: string
}
